import type { TaskFileTarget, TaskFileUrlOptions } from './types';
import { warnSdkUtilFallback } from './warn';

const WIDTH_PARAMETER = 'w';
const FILE_PROXY_PATH =
  /^\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const RELATIVE_FILE_PROXY_SOURCE =
  /^\/files\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:[?#]|$)/i;
const ABSOLUTE_HTTP_SOURCE = /^https?:\/\//i;
const UNSAFE_SOURCE_CHARACTERS = /[\s,\\]/;

export type FileProxySource = {
  fileId: string;
  /** True when the source already names a `w` parameter, valid or not. */
  hasWidth: boolean;
  /** The requested output width when the only `w` parameter is a valid one. */
  width: number | null;
};

function isOutputWidth(width: unknown): width is number {
  return typeof width === 'number' && Number.isSafeInteger(width) && width > 0;
}

export function fileUrl(target: TaskFileTarget, options?: TaskFileUrlOptions): string | null {
  const fileId =
    typeof target === 'string'
      ? target
      : target !== null && typeof target === 'object'
        ? target.id
        : undefined;

  if (typeof fileId !== 'string' || fileId === '') {
    warnSdkUtilFallback('file', 'No file id was provided; returning null.');
    return null;
  }

  const url = `/files/${encodeURIComponent(fileId)}`;
  const width = options?.width;
  const quality = options?.quality;
  const parameters: string[] = [];

  if (width !== undefined) {
    if (isOutputWidth(width)) {
      parameters.push(`${WIDTH_PARAMETER}=${width}`);
    } else {
      warnSdkUtilFallback('file', 'The width option must be a positive integer; ignoring it.');
    }
  }

  if (quality !== undefined) {
    if (isOutputWidth(quality) && quality <= 100) {
      parameters.push(`quality=${quality}`);
    } else {
      warnSdkUtilFallback(
        'file',
        'The quality option must be an integer from 1 to 100; using the default.'
      );
    }
  }

  return `${url}${parameters.length > 0 ? `?${parameters.join('&')}` : ''}`;
}

export function normalizeFileHost(host: string): string {
  return host.trim().toLowerCase().replace(/\.$/, '');
}

function isWidthSegment(segment: string): boolean {
  return segment.split('=', 1)[0] === WIDTH_PARAMETER;
}

function widthParametersIn(query: string): string[] {
  return query
    .split('&')
    .filter(isWidthSegment)
    .map((segment) => segment.slice(WIDTH_PARAMETER.length + 1));
}

function requestedWidthIn(values: string[]): number | null {
  const [value] = values;

  if (values.length !== 1 || value === undefined || !/^[1-9][0-9]*$/.test(value)) {
    return null;
  }

  const width = Number(value);

  return isOutputWidth(width) ? width : null;
}

/**
 * A quick check for the proxy path in a relative or absolute source. It does not
 * decide whether the host belongs to this workspace.
 */
export function hasFileProxyPath(src: string): boolean {
  if (RELATIVE_FILE_PROXY_SOURCE.test(src)) {
    return true;
  }

  if (!ABSOLUTE_HTTP_SOURCE.test(src) || !src.includes('/files/')) {
    return false;
  }

  try {
    return FILE_PROXY_PATH.test(new URL(src).pathname);
  } catch {
    return false;
  }
}

/**
 * Recognizes a source that names this workspace's file proxy: a host-relative
 * `/files/{uuid}` path, or an absolute URL on the document origin or a trusted
 * workspace host. A matching path on any other host is not this workspace's
 * file, so it is not recognized.
 */
export function parseFileProxySource(
  src: string,
  trustedHosts: ReadonlySet<string>,
  documentOrigin: string
): FileProxySource | null {
  if (src === '' || UNSAFE_SOURCE_CHARACTERS.test(src)) {
    return null;
  }

  const isRelative = RELATIVE_FILE_PROXY_SOURCE.test(src);

  if (!isRelative && !ABSOLUTE_HTTP_SOURCE.test(src)) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(src, documentOrigin);
  } catch {
    return null;
  }

  if (!isRelative) {
    const trusted =
      url.origin === documentOrigin ||
      (url.port === '' && trustedHosts.has(normalizeFileHost(url.hostname)));

    if (url.username !== '' || url.password !== '' || !trusted) {
      return null;
    }
  }

  const fileId = FILE_PROXY_PATH.exec(url.pathname)?.[1];

  if (fileId === undefined) {
    return null;
  }

  const widthValues = widthParametersIn(url.search.slice(1));

  return { fileId, hasWidth: widthValues.length > 0, width: requestedWidthIn(widthValues) };
}

/**
 * Replaces the `w` parameter of a recognized proxy source, or removes it when
 * `width` is null. The origin, path, other parameters, and fragment keep their
 * authored text.
 */
export function withFileWidth(src: string, width: number | null): string {
  const hashIndex = src.indexOf('#');
  const hash = hashIndex === -1 ? '' : src.slice(hashIndex);
  const beforeHash = hashIndex === -1 ? src : src.slice(0, hashIndex);
  const queryIndex = beforeHash.indexOf('?');
  const base = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : beforeHash.slice(queryIndex + 1);
  const segments = query.split('&').filter((segment) => segment !== '' && !isWidthSegment(segment));

  if (width !== null) {
    if (!isOutputWidth(width)) {
      throw new RangeError(`A file width must be a positive integer; received ${width}.`);
    }

    segments.push(`${WIDTH_PARAMETER}=${width}`);
  }

  return `${base}${segments.length > 0 ? `?${segments.join('&')}` : ''}${hash}`;
}

/** Ascending, distinct, positive integer widths from an authorized `sizes` list. */
export function availableFileWidths(widths: readonly number[]): number[] {
  return [...new Set(widths.filter(isOutputWidth))].toSorted((a, b) => a - b);
}

/** A `srcset` value with one width descriptor for each supplied actual output width. */
export function fileSrcSet(src: string, widths: readonly number[]): string {
  return availableFileWidths(widths)
    .map((width) => `${withFileWidth(src, width)} ${width}w`)
    .join(', ');
}

/** The narrowest available width that covers `cssWidth`, or the widest one. */
export function fileWidthFor(widths: readonly number[], cssWidth: number): number | null {
  const available = availableFileWidths(widths);

  return available.find((width) => width >= cssWidth) ?? available.at(-1) ?? null;
}
